import type { ToolId } from '../config/toolConfig';

const STORAGE_KEY = 'rescue-roulette.stats.v1';

export interface ToolStats {
  uses: number;
  successes: number;
}

export interface RescueStatistics {
  total: number;
  successes: number;
  failures: number;
  toolStats: Record<ToolId, ToolStats>;
  totalRescueTime: number;
  humansSaved: number;
  animalsSaved: number;
  dazedCount: number;
  ropeErrors: number;
  vestErrors: number;
  perfectRescues: number;
  currentStreak: number;
  bestStreak: number;
  highScore: number;
}

const blankToolStats = (): Record<ToolId, ToolStats> => ({
  lifeRing: { uses: 0, successes: 0 },
  rope: { uses: 0, successes: 0 },
  lifeVest: { uses: 0, successes: 0 },
  rescuePole: { uses: 0, successes: 0 },
  inflatableBoat: { uses: 0, successes: 0 },
  autoSalvage: { uses: 0, successes: 0 },
});

export const createBlankStats = (): RescueStatistics => ({
  total: 0,
  successes: 0,
  failures: 0,
  toolStats: blankToolStats(),
  totalRescueTime: 0,
  humansSaved: 0,
  animalsSaved: 0,
  dazedCount: 0,
  ropeErrors: 0,
  vestErrors: 0,
  perfectRescues: 0,
  currentStreak: 0,
  bestStreak: 0,
  highScore: 0,
});

export class StatisticsSystem {
  data: RescueStatistics;

  constructor() {
    this.data = this.load();
  }

  private load(): RescueStatistics {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<RescueStatistics> | null;
      if (!parsed) return createBlankStats();
      const blank = createBlankStats();
      return {
        ...blank,
        ...parsed,
        toolStats: { ...blank.toolStats, ...(parsed.toolStats ?? {}) },
      };
    } catch {
      return createBlankStats();
    }
  }

  save(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
  }

  reset(): void {
    this.data = createBlankStats();
    this.save();
  }

  record(result: {
    success: boolean;
    perfect: boolean;
    tool: ToolId | null;
    elapsed: number;
    animal: boolean;
    score: number;
    dazed: boolean;
    ropeError: boolean;
    vestError: boolean;
  }): void {
    const stats = this.data;
    stats.total += 1;
    stats.totalRescueTime += result.elapsed;
    if (result.tool) stats.toolStats[result.tool].uses += 1;
    stats.dazedCount += result.dazed ? 1 : 0;
    stats.ropeErrors += result.ropeError ? 1 : 0;
    stats.vestErrors += result.vestError ? 1 : 0;
    stats.highScore = Math.max(stats.highScore, result.score);

    if (result.success) {
      stats.successes += 1;
      if (result.tool) stats.toolStats[result.tool].successes += 1;
      stats.currentStreak += 1;
      stats.bestStreak = Math.max(stats.bestStreak, stats.currentStreak);
      stats.perfectRescues += result.perfect ? 1 : 0;
      if (result.animal) stats.animalsSaved += 1;
      else stats.humansSaved += 1;
    } else {
      stats.failures += 1;
      stats.currentStreak = 0;
    }
    this.save();
  }
}
